/**
 * Group questions by section and format them for react-select
 * @param {Array} sections - Sections containing questions
 * @param {Array} usedQuestionIds - IDs of the questions already used
 * @returns {Array} - Formatted options for react-select
 */
export const formatQuestionOptionsBySection = (sections = [], usedQuestionIds = []) => {
  if (!sections || !sections.length)
    return []

  const validQuestionContexts = ['grade', 'selection', 'selection_multi', 'variable']

  return sections
    .map(section => {
      const availableQuestions = section.questions
        .filter(question =>
          validQuestionContexts.includes(question.context) &&
          !usedQuestionIds.includes(question.id.toString())
        )

      if (!availableQuestions.length)
        return null

      return {
        label: section.name,
        options: availableQuestions.map(question => ({
          value: question.id,
          label: question.title
        }))
      }
    })
    .filter(Boolean)
}

/**
 * Format the options for the values of a variable
 * @param {String} variableId - ID of the variable
 * @param {Object} variableValues - Values of the variable
 * @returns {Array} - Formatted options for react-select
 */
export const formatVariableValues = (variableId, variableValues) => {
  if (!variableValues[variableId])
    return []


  const values = [... new Set(variableValues[variableId].map(value => value.value.split(';')).flat())]
  const formattedValues = values.map(value => ({ value, label: value }))
  formattedValues.push({ value: '__EMPTY__', label: '(vide)' })
  return formattedValues.sort((first, second) => first.label.localeCompare(second.label))
}

const formatOptionsValues = options => {
  if (!options)
    return []

  const formattedValues = options.map(option => ({ value: option.id, label: option.content }))
  formattedValues.push({ value: '__EMPTY__', label: '(vide)' })
  return formattedValues.sort((first, second) => first.label.localeCompare(second.label))
}

/**
 * Format the options for the values of a question
 * @param {Object} question - Question for which we want the options
 * @returns {Array} - Formatted options for react-select
 */
export const formatQuestionValueOptions = (question, variableValues = {}) => {
  if (!question)
    return []


  const isVariable = question.context === 'variable' && question.variable_id
  if (isVariable)
    return formatVariableValues(question.variable_id, variableValues)
  return formatOptionsValues(question.options)
}

/**
 * Find a question by its ID among the sections
 * @param {Array} sections - Sections containing questions
 * @param {String|Number} questionId - ID of the question to find
 * @returns {Object|null} - Found question or null
 */
export const findQuestionById = (questionId, sections = []) => {
  if (!sections || !sections.length || !questionId)
    return null

  for (const section of sections) {
    const foundQuestion = section.questions.find(question =>
      question.id.toString() === questionId.toString()
    )
    if (foundQuestion)
      return foundQuestion
  }

  return null
}

/**
 * Format the options for the variables
 * @param {Array} variables - List of variables
 * @param {Array} usedVariableIds - IDs of the variables already used
 * @returns {Array} - Formatted options for react-select
 */
export const formatVariableOptions = (variables = [], usedVariableIds = []) => {
  if (!variables || !variables.length)
    return []

  return variables
    .filter(variable => !usedVariableIds.includes(variable.id.toString()))
    .map(variable => ({
      value: variable.id,
      label: variable.name
    }))
}

/**
 * Format filter values based on question type
 * @param {Array} values - Values to format
 * @param {Object} question - Question object
 * @param {Array} questionValueOptions - Value options for the question
 * @returns {Array} - Formatted values for react-select
 */
export const formatFilterValues = (values, question, questionValueOptions) => {
  if (!values || !question)
    return []

  const formattedFilterValues = values.map(value => {
    const option = questionValueOptions.find(opt =>
      opt.value.toString() === value.toString()
    )
    return option ? { value: option.value, label: option.label } : null
  }).filter(Boolean)

  return formattedFilterValues
}
